<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore } from 'vuex';
import waCallsApi from 'dashboard/api/waCallsApi';

const { t } = useI18n();
const router = useRouter();
const { accountId } = useAccount();
const store = useStore();

const DEFAULT_WACALLS_URL =
  window.chatwootConfig?.WACALLS_URL || 'http://localhost:8080';

const loading = ref(false);
const showAdvanced = ref(false);

const formData = ref({
  inboxName: 'WaCalls',
  wacallsUrl: DEFAULT_WACALLS_URL,
});

const wacallsUrlFromEnv = computed(
  () => window.chatwootConfig?.WACALLS_URL || DEFAULT_WACALLS_URL
);

const createInbox = async () => {
  if (!formData.value.inboxName.trim()) {
    useAlert(t('INBOX_MGMT.ADD.WACALLS.ERRORS.NAME_REQUIRED'));
    return;
  }
  if (!formData.value.wacallsUrl.trim()) {
    useAlert(t('INBOX_MGMT.ADD.WACALLS.ERRORS.URL_REQUIRED'));
    return;
  }

  try {
    loading.value = true;

    const inbox = await store.dispatch('inboxes/createChannel', {
      channel: {
        type: 'api',
        sub_type: 'wacalls',
        additional_attributes: {
          wacalls_url: formData.value.wacallsUrl,
          is_wacalls: true,
        },
      },
      name: formData.value.inboxName,
      channel_type: 'Channel::Api',
    });

    // eslint-disable-next-line no-use-before-define
    await autoConfigureWaCalls(inbox);

    useAlert(t('INBOX_MGMT.ADD.WACALLS.SUCCESS'));

    setTimeout(() => {
      router.push({
        name: 'settings_inbox_show',
        params: { accountId: accountId.value, inboxId: inbox.id },
      });
    }, 1000);
  } catch (err) {
    const msg =
      err.response?.data?.message ||
      err.message ||
      t('INBOX_MGMT.ADD.WACALLS.ERRORS.CREATE_FAILED');
    useAlert(msg);
  } finally {
    loading.value = false;
  }
};

const autoConfigureWaCalls = async inbox => {
  try {
    const currentUser = store.getters['auth/getCurrentUser'];
    const sessionName = formData.value.inboxName
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '_')
      .substring(0, 32);

    const session = await waCallsApi.createSession(accountId.value, {
      name: sessionName,
    });

    const sid = session?.id || session?.session_id;
    if (!sid) return;

    const chatwootResult = await waCallsApi.setChatwootConfig(
      accountId.value,
      sid,
      {
        url: window.location.origin,
        account_id: accountId.value,
        account_token: currentUser?.access_token || '',
        inbox_id: inbox.id,
        inbox_identifier: inbox.inbox_identifier || '',
      }
    );

    const webhookUrl = chatwootResult?.webhook_url || '';

    await store.dispatch('inboxes/updateInbox', {
      id: inbox.id,
      formData: false,
      channel: {
        webhook_url: webhookUrl,
        additional_attributes: {
          ...inbox.additional_attributes,
          wacalls_session_id: sid,
        },
      },
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('WaCalls auto-config parcial:', e.message);
  }
};

const goBack = () => {
  router.push({
    name: 'settings_inbox_new',
    params: { accountId: accountId.value },
  });
};
</script>

<template>
  <div class="flex flex-col h-full">
    <div
      class="flex items-center gap-4 p-6 border-b border-slate-75 dark:border-slate-700"
    >
      <div
        class="flex items-center justify-center size-12 rounded-full bg-green-100 dark:bg-green-900/30 flex-shrink-0"
      >
        <svg
          viewBox="0 0 24 24"
          class="size-6 text-green-600 dark:text-green-400"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.8 1.24h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 6 6l.96-.96a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"
          />
        </svg>
      </div>
      <div>
        <h2 class="text-2xl font-semibold text-slate-900 dark:text-slate-25">
          {{ $t('INBOX_MGMT.ADD.WACALLS.TITLE') }}
        </h2>
        <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">
          {{ $t('INBOX_MGMT.ADD.WACALLS.DESC') }}
        </p>
      </div>
    </div>

    <div class="flex-1 p-6 overflow-y-auto">
      <div class="max-w-2xl mx-auto">
        <div
          class="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800"
        >
          <h3
            class="mb-2 text-sm font-semibold text-blue-900 dark:text-blue-100"
          >
            {{ $t('INBOX_MGMT.ADD.WACALLS.ABOUT.TITLE') }}
          </h3>
          <ul
            class="text-sm text-blue-800 dark:text-blue-200 space-y-1 list-disc list-inside"
          >
            <li>{{ $t('INBOX_MGMT.ADD.WACALLS.ABOUT.ITEM_1') }}</li>
            <li>{{ $t('INBOX_MGMT.ADD.WACALLS.ABOUT.ITEM_2') }}</li>
            <li>{{ $t('INBOX_MGMT.ADD.WACALLS.ABOUT.ITEM_3') }}</li>
          </ul>
        </div>

        <form class="space-y-5" @submit.prevent="createInbox">
          <div>
            <label
              class="block mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-200"
            >
              {{ $t('INBOX_MGMT.ADD.WACALLS.INBOX_NAME.LABEL') }} *
            </label>
            <input
              v-model="formData.inboxName"
              type="text"
              required
              :placeholder="$t('INBOX_MGMT.ADD.WACALLS.INBOX_NAME.PLACEHOLDER')"
              class="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-woot-500 focus:border-transparent"
            />
            <p class="mt-1 text-xs text-slate-500">
              {{ $t('INBOX_MGMT.ADD.WACALLS.INBOX_NAME.HINT') }}
            </p>
          </div>

          <div class="border-t border-slate-200 dark:border-slate-700 pt-4">
            <button
              type="button"
              class="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
              @click="showAdvanced = !showAdvanced"
            >
              <svg
                class="size-4 transition-transform"
                :class="{ 'rotate-90': showAdvanced }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
              <span>{{
                showAdvanced
                  ? $t('INBOX_MGMT.ADD.WACALLS.HIDE_ADVANCED')
                  : $t('INBOX_MGMT.ADD.WACALLS.SHOW_ADVANCED')
              }}</span>
            </button>
          </div>

          <div
            v-if="showAdvanced"
            class="space-y-5 pl-4 border-l-2 border-slate-200 dark:border-slate-700"
          >
            <div>
              <label
                class="block mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-200"
              >
                {{ $t('INBOX_MGMT.ADD.WACALLS.SERVER_URL.LABEL') }}
              </label>
              <input
                v-model="formData.wacallsUrl"
                type="url"
                :placeholder="
                  $t('INBOX_MGMT.ADD.WACALLS.SERVER_URL.PLACEHOLDER')
                "
                class="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-woot-500 focus:border-transparent"
              />
              <p class="mt-1 text-xs text-slate-500">
                {{ $t('INBOX_MGMT.ADD.WACALLS.SERVER_URL.HINT') }}:
                {{ wacallsUrlFromEnv }}
              </p>
            </div>
          </div>

          <div
            class="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800"
          >
            <h4
              class="mb-2 text-sm font-medium text-green-900 dark:text-green-100"
            >
              {{ $t('INBOX_MGMT.ADD.WACALLS.AUTO_CREATE.TITLE') }}
            </h4>
            <ul
              class="text-sm text-green-800 dark:text-green-200 space-y-1 list-disc list-inside"
            >
              <li>{{ $t('INBOX_MGMT.ADD.WACALLS.AUTO_CREATE.ITEM_1') }}</li>
              <li>{{ $t('INBOX_MGMT.ADD.WACALLS.AUTO_CREATE.ITEM_2') }}</li>
              <li>{{ $t('INBOX_MGMT.ADD.WACALLS.AUTO_CREATE.ITEM_3') }}</li>
            </ul>
          </div>

          <div class="flex gap-3 pt-2">
            <button
              type="submit"
              :disabled="loading"
              class="px-6 py-2.5 text-sm font-medium text-white bg-woot-500 rounded-lg hover:bg-woot-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <svg
                v-if="loading"
                class="size-4 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                />
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              {{
                loading
                  ? $t('INBOX_MGMT.ADD.WACALLS.BUTTONS.CREATING')
                  : $t('INBOX_MGMT.ADD.WACALLS.BUTTONS.CREATE')
              }}
            </button>
            <button
              type="button"
              :disabled="loading"
              class="px-6 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-700 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50 transition-colors"
              @click="goBack"
            >
              {{ $t('GENERAL.CANCEL') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
